/**
 * 技能树效果聚合
 *
 * 单独成文件的原因：`simulate.ts`（结算）与 `query.ts`（UI 可用性）都要用同一份
 * 效果聚合结果，如果放在任一方里就会形成循环导入。放在这里两边都只依赖它。
 */

import type { SkillEffect, SkillNode } from "./types.js";

export interface SkillEffects {
  spreadSpeedMul: number;
  routeCostMul: number;
  energyRegenBonus: number;
  maxEnergyBonus: number;
  mistResist: number;
  /** 传播波及的区域数 */
  spreadRadiusCount: number;
  /** 额外显现的隐藏路线条数 */
  revealRouteCount: number;
}

export const EMPTY_EFFECTS: SkillEffects = {
  spreadSpeedMul: 1,
  routeCostMul: 1,
  energyRegenBonus: 0,
  maxEnergyBonus: 0,
  mistResist: 0,
  spreadRadiusCount: 0,
  revealRouteCount: 0,
};

export function collectEffects(tree: readonly SkillNode[], unlocked: readonly string[]): SkillEffects {
  const acc: SkillEffects = { ...EMPTY_EFFECTS };
  for (const id of unlocked) {
    const node = tree.find((n) => n.id === id);
    if (!node) continue;
    for (const effect of node.effects) {
      applyEffect(acc, effect);
    }
  }
  return acc;
}

function applyEffect(acc: SkillEffects, effect: SkillEffect): void {
  switch (effect.kind) {
    case "spreadSpeed":
      acc.spreadSpeedMul *= 1 + effect.value / 100;
      break;
    case "spreadRadius":
      acc.spreadRadiusCount += effect.value;
      break;
    case "energyRegen":
      acc.energyRegenBonus += effect.value;
      break;
    case "maxEnergy":
      acc.maxEnergyBonus += effect.value;
      break;
    case "mistResist":
      acc.mistResist += effect.value;
      break;
    case "revealRoute":
      acc.revealRouteCount += effect.value;
      break;
  }
}

/** 传播一次的成本（含技能折扣，最低 1 点） */
export function spreadCost(baseCost: number, effects: SkillEffects): number {
  return Math.max(1, Math.round(baseCost * effects.routeCostMul));
}
